import {
  useGetUserPreferencesQuery,
  useSetUserPreferenceMutation,
} from '@/store/features/user-preferences.feature';
import { useAuth } from '@clerk/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  layoutsEqual,
  parseLayout,
  parseLayoutMap,
  pinRef,
  placeRef,
  serializeLayout,
  SIDEBAR_LAYOUT_PREFERENCE,
  unpinRef,
  type SidebarLayout,
  type SidebarRef,
  type SidebarSection,
} from './sidebar-layout.util';
import {
  focusCandidates,
  focusedPinKey,
  refocusAfterRender,
} from './sidebar-focus.util';
import { useRetryOnError } from './use-retry-on-error.hook';

/** The organization key a user without an organization saves under. */
const NO_ORGANIZATION = 'personal';

const cacheKey = (userId: string, orgKey: string) =>
  `cleanlist:sidebar-layout:${userId}:${orgKey}`;

/** This browser's copy of the user's layout in an organization, or null. */
export function readCachedLayout(
  userId: string,
  orgKey: string
): SidebarLayout | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId, orgKey));
    return raw === null ? null : parseLayout(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Keeps this browser's copy in step; null removes it. */
export function writeCachedLayout(
  userId: string,
  orgKey: string,
  layout: SidebarLayout | null
): void {
  try {
    if (layout)
      localStorage.setItem(
        cacheKey(userId, orgKey),
        JSON.stringify(serializeLayout(layout))
      );
    else localStorage.removeItem(cacheKey(userId, orgKey));
  } catch {
    // Storage blocked or full: the first paint uses the defaults instead.
  }
}

export interface SidebarLayoutState {
  /** The user's layout, or the defaults while they have none. */
  layout: SidebarLayout;
  /** True while the user has no layout of their own. */
  isDefault: boolean;
  /**
   * Changes are taken: the saved layout is back, and the defaults are
   * complete where they apply. Until then every change is ignored, so none
   * is built on a layout the server does not hold.
   */
  ready: boolean;
  /** Puts a ref in a section, before another or at the end. */
  place: (
    ref: SidebarRef,
    section: SidebarSection,
    before?: SidebarRef | null
  ) => void;
  /** Pins a ref at the end of the rows. */
  pin: (ref: SidebarRef) => void;
  unpin: (ref: SidebarRef) => void;
  /** Rearranges the whole layout at once, such as every page to the rows. */
  arrange: (change: (layout: SidebarLayout) => SidebarLayout) => void;
  /** Back to the defaults, with Undo on a toast. */
  reset: () => void;
}

/** A layout on its way to the server, for one user in one organization. */
interface SidebarWrite {
  scope: string;
  userId: string;
  orgKey: string;
  layout: SidebarLayout | null;
}

/**
 * The user's sidebar layout in the current organization, saved per user in
 * the user preferences (`sidebar_layout_v1`, organization id to layout,
 * each marked with its version) so it follows them across devices and pins
 * never cross organizations.
 *
 * Until the preferences load, the sidebar paints from this browser's copy,
 * else the defaults, and takes no changes; a failed read is tried again.
 * `defaultsReady` is false while the defaults still miss something, such as
 * the favorite agents before the agents load. Changes show at once and roll
 * back with a toast when the save fails. Nothing is written until the user
 * changes something, and a reset saves no layout at all, so the defaults
 * keep applying.
 */
export function useSidebarLayout(
  defaults: SidebarLayout,
  { defaultsReady = true }: { defaultsReady?: boolean } = {}
): SidebarLayoutState {
  const { userId, orgId } = useAuth();
  const orgKey = orgId ?? NO_ORGANIZATION;
  const scope = userId ? `${userId}:${orgKey}` : null;

  const { data, isSuccess, isError, refetch } = useGetUserPreferencesQuery(
    undefined,
    // The API answers 403 until the user has an organization.
    { skip: !userId || !orgId, refetchOnReconnect: true }
  );
  useRetryOnError({ isError, isSuccess }, refetch);
  const [setPreference] = useSetUserPreferenceMutation();

  const savedMap = useMemo(
    () => parseLayoutMap(data?.preferences?.[SIDEBAR_LAYOUT_PREFERENCE]),
    [data]
  );
  // Undefined until the preferences are back; null when none is saved here.
  const saved = useMemo(
    () => (isSuccess ? parseLayout(savedMap[orgKey]) : undefined),
    [isSuccess, orgKey, savedMap]
  );
  const cached = useMemo(
    () => (userId ? readCachedLayout(userId, orgKey) : null),
    [orgKey, userId]
  );

  // The user's own changes by scope, shown before their saves are back; null
  // is a reset to the defaults.
  const [local, setLocal] = useState<Record<string, SidebarLayout | null>>({});
  const hasLocal = scope !== null && scope in local;
  const effective: SidebarLayout | null =
    scope !== null && hasLocal
      ? local[scope]
      : saved !== undefined
        ? saved
        : cached;
  const layout = effective ?? defaults;
  // A layout of the user's own needs no defaults; the defaults do.
  const ready = isSuccess && (effective !== null || defaultsReady);

  // The server's answer becomes this browser's copy for the next first paint.
  useEffect(() => {
    if (!userId || saved === undefined || hasLocal) return;
    writeCachedLayout(userId, orgKey, saved);
  }, [hasLocal, orgKey, saved, userId]);

  // Read at call time, so two changes in one tick build on each other.
  const current = useRef({ layout, effective, ready });
  current.current = { layout, effective, ready };
  // One save at a time: the server rewrites the whole preferences row per
  // request, so saves sent together could land in any order. While one is
  // out, only the newest layout of each scope waits to follow it.
  const inFlight = useRef(false);
  const queued = useRef(new Map<string, SidebarWrite>());
  // What a failed save goes back to, by scope: the last layout the server
  // accepted, or what the user saw before their first change.
  const confirmed = useRef(new Map<string, SidebarLayout | null>());

  const send = useCallback(() => {
    if (inFlight.current) return;
    const write = queued.current.values().next().value;
    if (!write) return;
    queued.current.delete(write.scope);
    inFlight.current = true;
    // Only this organization's entry: the server merges it into the saved
    // map, so a stale copy of another organization's layout never lands.
    // It carries the version, so an upgrade on read happens only until then.
    setPreference({
      key: SIDEBAR_LAYOUT_PREFERENCE,
      value: {
        [write.orgKey]: write.layout && serializeLayout(write.layout),
      },
    })
      .unwrap()
      .then(
        () => {
          confirmed.current.set(write.scope, write.layout);
          if (!queued.current.has(write.scope))
            writeCachedLayout(write.userId, write.orgKey, write.layout);
        },
        () => {
          // A newer change is waiting, and it carries this one with it.
          if (queued.current.has(write.scope)) return;
          const back = confirmed.current.get(write.scope) ?? null;
          // Rolling back remounts a moved item: keep focus on it.
          const focused = focusedPinKey();
          setLocal((prev) => ({ ...prev, [write.scope]: back }));
          if (focused)
            refocusAfterRender(
              focusCandidates(focused, document),
              () => document
            );
          toast.error("Couldn't save the sidebar");
        }
      )
      .finally(() => {
        inFlight.current = false;
        send();
      });
  }, [setPreference]);

  // The scope on screen now, for an Undo pressed after a switch.
  const scopeNow = useRef(scope);
  scopeNow.current = scope;

  /** Shows and saves a change; false when there was nothing to change. */
  const commit = useCallback(
    (next: SidebarLayout | null): boolean => {
      if (!userId || !scope) return false;
      const before = current.current;
      if (!before.ready) return false;
      if (
        next === null
          ? before.effective === null
          : layoutsEqual(next, before.layout)
      )
        return false;
      if (!confirmed.current.has(scope))
        confirmed.current.set(scope, before.effective);
      setLocal((prev) => ({ ...prev, [scope]: next }));
      current.current = {
        ...before,
        layout: next ?? defaults,
        effective: next,
      };
      queued.current.set(scope, { scope, userId, orgKey, layout: next });
      send();
      return true;
    },
    [defaults, orgKey, scope, send, userId]
  );

  const place = useCallback(
    (ref: SidebarRef, section: SidebarSection, before?: SidebarRef | null) =>
      void commit(
        placeRef(current.current.layout, ref, section, before ?? null)
      ),
    [commit]
  );
  const pin = useCallback(
    (ref: SidebarRef) => void commit(pinRef(current.current.layout, ref)),
    [commit]
  );
  const unpin = useCallback(
    (ref: SidebarRef) => void commit(unpinRef(current.current.layout, ref)),
    [commit]
  );
  const arrange = useCallback(
    (change: (layout: SidebarLayout) => SidebarLayout) =>
      void commit(change(current.current.layout)),
    [commit]
  );
  const commitNow = useRef(commit);
  commitNow.current = commit;
  // One click throws away the whole layout on every device, so it can be
  // taken back, in the organization it was reset in.
  const reset = useCallback(() => {
    const previous = current.current.effective;
    const resetIn = scope;
    if (!previous || !commit(null)) return;
    toast('Sidebar reset', {
      action: {
        label: 'Undo',
        onClick: () => {
          if (scopeNow.current === resetIn) commitNow.current(previous);
        },
      },
    });
  }, [commit, scope]);

  const isDefault = effective === null;
  return useMemo(
    () => ({ layout, isDefault, ready, place, pin, unpin, arrange, reset }),
    [arrange, isDefault, layout, pin, place, ready, reset, unpin]
  );
}
